import type { ISODate, WeekStart } from "../domain/entities";

const dateParts = (date: Date, timeZone: string) => Object.fromEntries(
  new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date).filter((part) => part.type !== "literal").map((part) => [part.type, part.value]),
);

export function dateInTimeZone(date: Date, timeZone: string): ISODate {
  const parts = dateParts(date, timeZone);
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function localInputInTimeZone(instant: string | Date, timeZone: string) {
  const parts = dateParts(typeof instant === "string" ? new Date(instant) : instant, timeZone);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

export function zonedInputToISOString(value: string, timeZone: string) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) throw new Error("The date and time format is invalid.");
  const [, year, month, day, hour, minute] = match;
  const desired = Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));
  let instant = desired;
  for (let index = 0; index < 2; index += 1) {
    const parts = dateParts(new Date(instant), timeZone);
    const represented = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute));
    instant += desired - represented;
  }
  return new Date(instant).toISOString();
}

export function addDays(date: ISODate, amount: number): ISODate {
  const value = new Date(`${date}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + amount);
  return value.toISOString().slice(0, 10);
}

export function startOfWeek(date: ISODate, weekStartsOn: WeekStart): ISODate {
  const value = new Date(`${date}T12:00:00Z`);
  const distance = (value.getUTCDay() - weekStartsOn + 7) % 7;
  return addDays(date, -distance);
}

export function dateLabel(date: ISODate, timeZone: string, options: Intl.DateTimeFormatOptions) {
  return new Intl.DateTimeFormat("en-US", { ...options, timeZone }).format(new Date(`${date}T12:00:00Z`));
}
